import { useEffect, useRef } from 'react';

import { ImpactAiPreset, PresetFormData } from '../types';
import { inferPrivilegesFromActions } from '../utils/inferPrivilegesFromActions';

interface UseAutoSetPrivilegesProps {
  preset: Partial<ImpactAiPreset>;
  formData: PresetFormData;
  onPrivilegesChange: (privileges: string[]) => void;
  enabled?: boolean;
}

/**
 * Hook that automatically sets required privileges based on navigate actions in the preset
 */
export const useAutoSetPrivileges = ({
  preset,
  formData,
  onPrivilegesChange,
  enabled = true,
}: UseAutoSetPrivilegesProps) => {
  const previousActionsRef = useRef<string>('');
  const userHasManuallyEditedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled || !preset.actions) {
      return;
    }

    // Convert actions to string for comparison
    const actionsString = JSON.stringify(preset.actions);

    // Check if actions have changed
    if (actionsString === previousActionsRef.current) {
      return;
    }

    previousActionsRef.current = actionsString;

    // Infer privileges from actions
    const inferredPrivileges = inferPrivilegesFromActions(preset.actions);

    // If no privileges were inferred, don't change anything
    if (inferredPrivileges.length === 0) {
      return;
    }

    // Check if user has manually edited privileges
    const currentPrivileges = formData.requiredPrivileges || [];
    const hasManualEdits = currentPrivileges.some((priv) => !inferredPrivileges.includes(priv));

    // If user has made manual edits, don't override them
    if (hasManualEdits && userHasManuallyEditedRef.current) {
      return;
    }

    // Merge inferred privileges with existing ones (avoiding duplicates)
    const mergedPrivileges = Array.from(new Set([...currentPrivileges, ...inferredPrivileges]));

    // Only update if there are new privileges to add
    if (mergedPrivileges.length > currentPrivileges.length) {
      onPrivilegesChange(mergedPrivileges);
    }
  }, [preset.actions, formData.requiredPrivileges, onPrivilegesChange, enabled]);

  // Track when user manually edits privileges
  useEffect(() => {
    const currentPrivileges = formData.requiredPrivileges || [];
    const inferredPrivileges = inferPrivilegesFromActions(preset.actions || []);

    // If current privileges contain items not in inferred, user has edited
    const hasManualEdits = currentPrivileges.some((priv) => !inferredPrivileges.includes(priv));

    if (hasManualEdits) {
      userHasManuallyEditedRef.current = true;
    }
  }, [formData.requiredPrivileges, preset.actions]);

  // Reset manual edit flag when preset changes
  useEffect(() => {
    userHasManuallyEditedRef.current = false;
  }, [preset.id]);

  return {
    inferredPrivileges: inferPrivilegesFromActions(preset.actions || []),
    hasManualEdits: userHasManuallyEditedRef.current,
  };
};
